import {Observable, timer} from 'rxjs';
import {
    filter,
    first,
    map,
    mapTo,
    mergeMap,
    switchMap,
    take,
    withLatestFrom
} from 'rxjs/operators';
import {TestScheduler} from 'rxjs/testing';

import Config          from '../../Config/Config';
import ConfigWatermark from '../../Config/ConfigWatermark';
import Playback        from '../../Playback/Playback';
import WatermarkStatus from '../Constants/WatermarkStatus';

const corners = [
    WatermarkStatus.BOTTOM_LEFT,
    WatermarkStatus.BOTTOM_RIGHT,
    WatermarkStatus.TOP_LEFT,
    WatermarkStatus.TOP_RIGHT
];

/**
 * Creates a randomised value representing the time until the watermark
 * will be shown, by adding a wobble (a random value within the wobble range),
 * +/- a wobble offset to the interval duration.
 */

const getTimeToShow = ({
    intervalDuration,
    wobbleRangeDuration,
    wobbleOffsetDuration
}: ConfigWatermark): number => {
    const wobble = (Math.random() * wobbleRangeDuration) + wobbleOffsetDuration;

    return intervalDuration + wobble;
};

/**
 * Creates a randomised value representing the visible duration of the
 * watermark once shown, based on a random value within the display range,
 * +/- the display offset.
 */

const getTimeToHide = ({
    displayOffsetDuration,
    displayRangeDuration
}: ConfigWatermark): number => {
    const displayDuration = (Math.random() * displayRangeDuration) + displayOffsetDuration;

    return displayDuration;
};

/**
 * Gets a random corner in which to position the watermark.
 */

const getCorner = (): WatermarkStatus => {
    const index = Math.floor(Math.random() * corners.length);

    return corners[index];
};

/**
 * Creates an observable that emits once per the provided interval
 * duration. Emits the configuration for use by the next stream factory.
 */

const createIntervalStream = (
    latestWatermarkConfig: ConfigWatermark,
    scheduler?: TestScheduler
): Observable<ConfigWatermark> =>
    timer(0, latestWatermarkConfig.intervalDuration, scheduler)
        .pipe(
            mapTo(latestWatermarkConfig)
        );

/**
 * Creates an observable that emits after a randomised duration defined
 * by `getTimeToShow`, after which the watermark is shown. Emits the
 * configuration for use by the next stream factory.
 */

const createWaitToShowWatermarkStream = (
    latestWatermarkConfig: ConfigWatermark,
    scheduler?: TestScheduler
): Observable<ConfigWatermark> =>
    timer(getTimeToShow(latestWatermarkConfig), scheduler)
        .pipe(
            mapTo(latestWatermarkConfig)
        );

/**
 * Creates an observable that emits once initially (showing the watermark),
 * and then again after a duration defined by `getTimeToHide()` (hiding the
 * watermark). Emits the watermark status (a randomised corner if shown, or
 * `HIDDEN` if hidden).
 */

const createShowAndHideWatermarkStream = (
    latestWatermarkConfig: ConfigWatermark,
    scheduler?: TestScheduler
): Observable<WatermarkStatus> =>
    timer(0, getTimeToHide(latestWatermarkConfig), scheduler)
        .pipe(
            take(2),
            map(count => count > 0 ? WatermarkStatus.HIDDEN : getCorner())
        );

/**
 * Creates a stream that emits in order to show or hide the watermark. As
 * the timing between these events is heavily randomised, please see the comments
 * below for implementation details.
 */

const createWatermarkStatusStream = (
    configUpdate$: Observable<Config>,
    playbackUpdate$: Observable<Playback>,
    scheduler?: TestScheduler
): Observable<WatermarkStatus> =>
    configUpdate$
        .pipe(
            // Obtain the latest configuration
            first(),
            // Provide the config to a new stream, emitting at a recurring
            // interval
            switchMap(({watermark}) => createIntervalStream(watermark, scheduler)),
            // On emission, create a timer which will emit once
            // the watermark should show
            mergeMap(watermark => createWaitToShowWatermarkStream(watermark, scheduler)),
            // On emission, show the watermark (emit a random corner)
            // then create a timer which emits once the watermark should
            // hide (emit hidden)
            switchMap(watermark => createShowAndHideWatermarkStream(watermark, scheduler)),
            // Combine watermark status emissions with the current
            // playback state
            withLatestFrom(playbackUpdate$),
            // Filter emissions so that the watermark is only shown
            // when a video is playing
            filter(([watermarkStatus, latestPlaybackValue]) =>
                watermarkStatus === WatermarkStatus.HIDDEN || latestPlaybackValue.isPlaying
            ),
            // Emit the watermark status only
            map(([watermarkStatus]) => watermarkStatus)
        );

export default createWatermarkStatusStream;